import {
  InitialEditorStateType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { LexicalEditor } from 'lexical'
import _ from 'lodash'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import COLORS from 'pared/constants/colors'

import { useVariables } from '../variables'
import SelectPlugin, { SelectNode } from './selectPlugin'

export interface IPropsType {
  type: 'text-editor'
  value: string
  onChange: string
  errorMessage?: string
  options: string
  hide?: string
  label?: string
  placeholder?: string
  width?: string
  margin?: string
}

const StyledFormControl = styled(FormControl)<{
  styleMargin?: string
}>`
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  gap: 25px;
  ${({ styleMargin }) => (!styleMargin ? '' : `margin: ${styleMargin};`)}
`

const Label = styled.div`
  display: inline-block;
  padding: 8.5px 0px;
  font-family: Lexend-Regular;
  font-size: 14px;
  line-height: 24px;
  font-style: normal;
  font-weight: 400;
  white-space: nowrap;
`

const StyledContentEditable = styled(ContentEditable)<{
  width?: string
  error: boolean
}>`
  padding: 8.5px 14px;
  min-width: 200px;
  ${({ width }) => (!width ? '' : `width: ${width};`)};
  font-family: Lexend-Regular;
  font-size: 16px;
  border: 1px solid ${({ error }) =>
    error ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'};
  line-height: 24px;
  outline: 0px;

  p:has(br:only-child):before,
  &:empty:before {
    content: var(--data-placeholder);
    color: #A2A2A2;
  }

  &:hover {
    border: 1px solid rgba(0,0,0,0.87);
  }

  &:focus {
    padding 7.5px 13px;
    border: 2px solid #1976d2;
  }

  & > * {
    margin: 0px;
  }

  span[data-lexical-select] {
    padding: 0px 10px;
    background: ${COLORS.Expo};
    color: ${COLORS.background};
    border-radius: 10px;
    font-size: 14px;
  }
`

const ErrorMessage = styled(FormHelperText)`
  margin: 4px 14px 0px;
`

const TextEditor = ({
  label,
  placeholder,
  width,
  margin,
  options,
  hide,
  ...props
}: IPropsType) => {
  const { variables, template } = useVariables()
  const { value, errorMessage } = useMemo(
    () => ({
      value: _.get(variables, props.value) as InitialEditorStateType,
      errorMessage: props.errorMessage && _.get(variables, props.errorMessage),
    }),
    [props, variables],
  )
  const onChange = useMemo(
    () =>
      _.get(variables, props.onChange) as (
        value: InitialEditorStateType,
      ) => void,
    [props, variables],
  )
  const editorRef = useRef<LexicalEditor | null>(null)

  useEffect(() => {
    if (editorRef.current && typeof value === 'string') {
      const editorState = editorRef.current.parseEditorState(value)

      editorRef.current.setEditorState(editorState)
      onChange(editorState)
    }
  }, [value])

  if (hide && template(hide) === 'true') return null

  return (
    <StyledFormControl styleMargin={margin} variant="outlined" margin="dense">
      {!label ? null : <Label>{template(label)}</Label>}

      <div>
        <LexicalComposer
          initialConfig={{
            namespace: 'text-editor',
            nodes: [SelectNode],
            editorState: value,
            onError: console.error,
          }}
        >
          <EditorRefPlugin editorRef={editorRef} />
          <RichTextPlugin
            contentEditable={
              <StyledContentEditable
                width={width}
                error={Boolean(errorMessage)}
                style={{
                  // @ts-ignore FIXME: https://github.com/facebook/lexical/issues/4113, we use css variables to hotfix this problem
                  '--data-placeholder': `"${placeholder}"`,
                }}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={onChange} />
          <SelectPlugin options={options} />
          <HistoryPlugin />
        </LexicalComposer>

        {!errorMessage ? null : (
          <ErrorMessage error>{errorMessage}</ErrorMessage>
        )}
      </div>
    </StyledFormControl>
  )
}

export default TextEditor
